import { TransportMode } from '../../../common/constants';
import { Coordinates, MapPoint, MapStop } from '../../../common/models';
import { MapPolyline, StopMarkerOptions } from './tracking-map.model';
import { MAP_ITEM_STYLES } from './tracking-map.constants';
import { GpsMapMarkerOptions, GpsMapPolylineOptions } from './gps-tracking-map.model';
import { ShipmentMarkerOptions } from './shipment-transport-order-map.model';

export class MapCoordinates {
  lng: number;
  lat: number;

  constructor(params?: Partial<MapStop | MapPoint>) {
    if (params) {
      const { longitude, latitude } = params;
      this.lng = longitude;
      this.lat = latitude;
    }
  }
}

export class PointMarkerOptions implements google.maps.MarkerOptions {
  position: MapCoordinates;
  icon: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 4,
    fillColor: MAP_ITEM_STYLES.pointFillColor,
    strokeWeight: 1,
    strokeColor: MAP_ITEM_STYLES.pointStrokeColor,
    fillOpacity: 1,
  };
  zIndex = 1;

  constructor(params?: MapPoint) {
    if (params) {
      this.position = new MapCoordinates(params);
    }
  }
}

export class VehicleMarkerOptions implements google.maps.MarkerOptions {
  position: MapCoordinates;
  zIndex = 1000;
  icon: google.maps.Icon;

  constructor(params?: Coordinates, transportMode?: TransportMode, options?: { height: number; width: number }) {
    if (params) {
      const url = 'assets/core/icons/pin_' + transportMode?.toLowerCase() + '.svg';

      this.position = new MapCoordinates(params);
      this.icon = {
        url,
        anchor: new google.maps.Point(22, 22),
        scaledSize: {
          ...MAP_ITEM_STYLES.vehicleIconSize,
          ...options,
          // just interface implementation
          equals(_other: google.maps.Size | null): boolean {
            return true;
          },
        },
      };
    }
  }
}

export class VehicleDirectionMarkerOptions implements google.maps.MarkerOptions {
  position: MapCoordinates;
  zIndex = 999;
  icon: google.maps.Icon;

  constructor(currentPoint: Coordinates, destinationPoint: Coordinates) {
    if (currentPoint && destinationPoint) {
      this.position = new MapCoordinates(currentPoint);
      this.icon = new GpsMapVehicleDirectionIcon(currentPoint, destinationPoint);
    }
  }
}

export class GpsMapVehicleDirectionIcon implements google.maps.Icon {
  url: string | null = null;
  anchor = new google.maps.Point(70, 70);
  scaledSize = {
    width: 140,
    height: 140,
    // just interface implementation
    equals(_other: google.maps.Size | null): boolean {
      return true;
    },
  };

  constructor(startPoint: Coordinates, destinationPoint: Coordinates) {
    if (startPoint && destinationPoint) {
      this.url = this.getIcon(
        this.calculateDestinationIconAngle(
          startPoint.latitude,
          startPoint.longitude,
          destinationPoint.latitude,
          destinationPoint.longitude,
        ),
      );
    }
  }

  private getIcon(degrees: number): string {
    const svg = `
    <svg width="198" height="199" viewBox="0 0 198 199" fill="none" xmlns="http://www.w3.org/2000/svg"  style="transform: rotate(${degrees}deg)">
      <path d="M49.5025 49.7746C56.0026 43.2745 63.7194 38.1183 72.2121 34.6005C80.7049 31.0827 89.8075 29.2721 99 29.2721C108.193 29.2721 117.295 31.0827 125.788 34.6005C134.281 38.1183 141.997 43.2745 148.497 49.7746L99 99.2721L49.5025 49.7746Z" fill="url(#paint0_radial_2227_7)"/>
      <defs>
          <radialGradient id="paint0_radial_2227_7" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(99.8114 98.4606) rotate(-45) scale(68.8525 70)">
            <stop stop-color="#0096E8"/>
            <stop offset="1" stop-color="#005482" stop-opacity="0"/>
          </radialGradient>
      </defs>
    </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  private calculateDestinationIconAngle(startLat: number, startLon: number, destLat: number, destLon: number): number {
    const toRadians = (latLng: number): number => {
      return (latLng * Math.PI) / 180;
    };

    const toDegrees = (radians: number): number => {
      return (radians * 180) / Math.PI;
    };

    const sphericalTo2dCoordinatesCoefficient = 0.69829196926;
    const rad_1 = toRadians(startLat);
    const rad_2 = toRadians(destLat);
    const deltaRad = toRadians(destLon - startLon);

    const y = Math.sin(deltaRad) * Math.cos(rad_2);
    const x = Math.cos(rad_1) * Math.sin(rad_2) - Math.sin(rad_1) * Math.cos(rad_2) * Math.cos(deltaRad);

    const degrees = toDegrees(Math.atan2(y, x));

    return degrees + degrees * sphericalTo2dCoordinatesCoefficient;
  }
}

export interface MapConfig {
  vehicleMarkerOptions: VehicleMarkerOptions;
  stopMarkerOptions: StopMarkerOptions[];
  stopPolylineOptions: MapPolyline[];
  pointMarkerOptions: PointMarkerOptions[];
  pointPolylineOptions: MapPolyline;
}

export interface GpsMapConfig {
  vehicleMarkerOptions: { icon: VehicleMarkerOptions; direction: VehicleDirectionMarkerOptions };
  markerOptions: GpsMapMarkerOptions[];
  polylineOptions: Array<{ line: GpsMapPolylineOptions; border: GpsMapPolylineOptions }>;
}

export interface ShipmentTransportOrderMapConfig {
  stopMarkerOptions: ShipmentMarkerOptions[];
  stopPolylineOptions: MapPolyline[];
}
